import {memo, useEffect, useRef, useState} from 'react'
import type {ProgressiveDiffEntry} from '../../types/progressive-diff-types'
import {DiffEntryLoadingSkeleton} from './DiffEntryLoadingSkeleton'
import {ObservableBox} from '@github-ui/use-sticky-header/ObservableBox'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import type {ProgressiveDiffManager} from '../../hooks/use-progressive-diff-manager'

export const LazyDiffEntryLoadingSkeleton = memo(function LazyDiffEntryLoadingSkeleton({
  progressiveDiffEntry,
  loadMore,
}: {
  progressiveDiffEntry: ProgressiveDiffEntry
  loadMore: ProgressiveDiffManager['loadMore']
}) {
  const [isLoading, setIsLoading] = useState(false)

  const abortControllerRef = useRef<AbortController | undefined>(undefined)

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  // Load entry when it enters viewport
  const [observe, unobserve] = useIntersectionObserver(entries => {
    if (entries[0]?.isIntersecting && !isLoading) {
      setIsLoading(true)
      loadMore({startingAt: progressiveDiffEntry, signal: abortControllerRef.current?.signal})
    }
  })

  return (
    <>
      <DiffEntryLoadingSkeleton
        ariaLabel={`Loading ${progressiveDiffEntry.path}`}
        testId={`lazy-load-${progressiveDiffEntry.path}`}
      />
      <ObservableBox onObserve={observe} onUnobserve={unobserve} />
    </>
  )
})

try{ LazyDiffEntryLoadingSkeleton.displayName ||= 'LazyDiffEntryLoadingSkeleton' } catch {}